import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  BarChart3,
  Briefcase,
  Check,
  ExternalLink,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Users,
} from "lucide-react";
import { useState } from "react";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQueryParams } from "@/hooks/use-query-params";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

type NotionPosition = {
  notionId: string;
  jobTitle: string | null;
  hiringManagerEmail: string | null;
  jdLink: string | null;
  stages: any[];
  linkedJob: { id: string; title: string; status: string; department: string } | null;
  syncState: { status: string; syncedAt: string } | null;
};

type LocalJob = {
  id: string;
  title: string;
  status: string;
  department: string;
  notionId: string | null;
  hiringManagerName: string | null;
};

type NotionCandidate = {
  notionId: string;
  importData: {
    name: string;
    email: string | null;
    phone: string | null;
    linkedinUrl: string | null;
    resumeUrl: string | null;
  };
  context: {
    status: string | null;
    source: string | null;
    positionIds: string[];
    currentStageIds: string[];
    entryDate: string | null;
    cvSummary: string | null;
  };
  positionInfo: { notionId: string; name: string } | null;
  linkedJob: { id: string; title: string } | null;
  existingApplicant: { id: string; fullName: string; email: string } | null;
  syncState: { status: string; syncedAt: string } | null;
};

type JobsSyncResponse = {
  notionPositions: NotionPosition[];
  localJobs: LocalJob[];
};

type CandidatesSyncResponse = {
  candidates: NotionCandidate[];
  localJobs: { id: string; title: string; status: string; notionId: string | null }[];
};

type SyncStatusResponse = {
  configured: boolean;
  connections: Record<string, { valid: boolean; error?: string }>;
  stats: {
    linkedJobs: number;
    importedCandidates: number;
    totalSyncRecords: number;
  };
};

export default function NotionSync({ embedded }: { embedded?: boolean }) {
  const { getParam, setParam } = useQueryParams();
  const notionTab = getParam("notionTab") || "status";
  return (
    <div className="space-y-6">
      {!embedded && (
        <PageHeader
          title="Sincronização Notion"
          description="Importe vagas e candidatos do Notion para a plataforma"
        />
      )}
      <Tabs value={notionTab} onValueChange={(tab) => setParam("notionTab", tab)}>
        <TabsList data-testid="tabs-notion-sync">
          <TabsTrigger value="status" data-testid="tab-status">
            <BarChart3 className="w-4 h-4 mr-2" />
            Status
          </TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs">
            <Briefcase className="w-4 h-4 mr-2" />
            Vagas
          </TabsTrigger>
          <TabsTrigger value="candidates" data-testid="tab-candidates">
            <Users className="w-4 h-4 mr-2" />
            Candidatos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="status">
          <StatusTab />
        </TabsContent>
        <TabsContent value="jobs">
          <JobsTab />
        </TabsContent>
        <TabsContent value="candidates">
          <CandidatesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatusTab() {
  const { data, isLoading } = useQuery<SyncStatusResponse>({
    queryKey: ["/api/sync/notion/status"],
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Vagas Vinculadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="text-linked-jobs">
              {data.stats.linkedJobs}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Candidatos Importados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="text-imported-candidates">
              {data.stats.importedCandidates}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Registros de Sync
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="text-total-sync">
              {data.stats.totalSyncRecords}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Conexões</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.entries(data.connections).map(([key, conn]) => (
            <div key={key} className="flex items-center gap-2 py-2">
              {conn.valid ? (
                <Check className="w-4 h-4 text-[hsl(var(--forest-pulse))]" />
              ) : (
                <AlertCircle className="w-4 h-4 text-[hsl(var(--alert-red))]" />
              )}
              <span className="capitalize">{key}</span>
              {conn.error && (
                <span className="text-sm text-[hsl(var(--alert-red))] ml-2">{conn.error}</span>
              )}
            </div>
          ))}
          {!data.configured && (
            <p className="text-sm text-muted-foreground mt-2">
              Configure as variáveis de ambiente POSITIONS_DB_ID e CANDIDATES_DB_ID para começar.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function JobsTab() {
  const { toast } = useToast();
  const [linkDialog, setLinkDialog] = useState<{ notionPosition: NotionPosition } | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string>("");

  const { data, isLoading, refetch } = useQuery<JobsSyncResponse>({
    queryKey: ["/api/sync/notion/jobs"],
  });

  const importMutation = useMutation({
    mutationFn: async (notionId: string) => {
      const res = await apiRequest("POST", `/api/sync/notion/jobs/${notionId}/import`);
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Vaga criada",
        description: `"${data.jobOpening.title}" foi importada com sucesso.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/sync/notion/jobs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sync/notion/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/job-openings"] });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao importar", description: err.message, variant: "destructive" });
    },
  });

  const linkMutation = useMutation({
    mutationFn: async ({ notionId, jobOpeningId }: { notionId: string; jobOpeningId: string }) => {
      const res = await apiRequest("POST", `/api/sync/notion/jobs/${notionId}/link`, {
        jobOpeningId,
      });
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Vaga vinculada",
        description: `Vinculada a "${data.jobOpening.title}" com sucesso.`,
      });
      setLinkDialog(null);
      setSelectedJobId("");
      queryClient.invalidateQueries({ queryKey: ["/api/sync/notion/jobs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sync/notion/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/job-openings"] });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao vincular", description: err.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-40" />
        ))}
      </div>
    );
  }

  const positions = data?.notionPositions || [];
  const localJobs = data?.localJobs || [];
  const unlinkedLocalJobs = localJobs.filter((j) => !j.notionId);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {positions.length} vagas encontradas no Notion
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          data-testid="button-refresh-jobs"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {positions.map((pos) => (
        <Card key={pos.notionId} data-testid={`card-notion-job-${pos.notionId}`}>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="space-y-1 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-lg">{pos.jobTitle || "Sem título"}</h3>
                  {pos.linkedJob && (
                    <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-[hsl(var(--forest-pulse))]">
                      <Check className="w-3 h-3" />
                      Vinculada
                    </span>
                  )}
                </div>
                {pos.hiringManagerEmail && (
                  <p className="text-sm text-muted-foreground">HM: {pos.hiringManagerEmail}</p>
                )}
                {pos.stages.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {pos.stages.length} etapa{pos.stages.length > 1 ? "s" : ""}:{" "}
                    {pos.stages.map((s) => s.stageName).join(", ")}
                  </p>
                )}
                {pos.jdLink && (
                  <a
                    href={pos.jdLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" /> Job Description
                  </a>
                )}
                {pos.linkedJob && (
                  <p className="text-sm text-[hsl(var(--forest-pulse))]">
                    Vinculada a: "{pos.linkedJob.title}" ({pos.linkedJob.department})
                  </p>
                )}
              </div>

              {!pos.linkedJob && (
                <div className="flex gap-2 ml-4 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => importMutation.mutate(pos.notionId)}
                    disabled={importMutation.isPending}
                    data-testid={`button-import-job-${pos.notionId}`}
                  >
                    {importMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <Plus className="w-4 h-4 mr-1" />
                    )}
                    Criar Nova
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setLinkDialog({ notionPosition: pos })}
                    disabled={unlinkedLocalJobs.length === 0}
                    data-testid={`button-link-job-${pos.notionId}`}
                  >
                    <Link2 className="w-4 h-4 mr-1" />
                    Associar
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {positions.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            Nenhuma vaga encontrada no Notion. Verifique se a integração está compartilhada com o
            database.
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!linkDialog}
        onOpenChange={(open) => {
          if (!open) {
            setLinkDialog(null);
            setSelectedJobId("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Associar a vaga existente</DialogTitle>
            <DialogDescription>
              Vincular "{linkDialog?.notionPosition.jobTitle}" do Notion a uma vaga existente na
              plataforma.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <SearchableSelect
              options={unlinkedLocalJobs.map((j) => ({
                value: j.id,
                label: `${j.title} (${j.department})`,
              }))}
              value={selectedJobId}
              onValueChange={setSelectedJobId}
              placeholder="Selecione uma vaga..."
              data-testid="select-link-job"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setLinkDialog(null);
                setSelectedJobId("");
              }}
              data-testid="button-cancel-link"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (linkDialog && selectedJobId) {
                  linkMutation.mutate({
                    notionId: linkDialog.notionPosition.notionId,
                    jobOpeningId: selectedJobId,
                  });
                }
              }}
              disabled={!selectedJobId || linkMutation.isPending}
              data-testid="button-confirm-link"
            >
              {linkMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CandidatesTab() {
  const { toast } = useToast();
  const [importDialog, setImportDialog] = useState<{ candidate: NotionCandidate } | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [selectedStageId, setSelectedStageId] = useState<string>("");

  const { data, isLoading, refetch } = useQuery<CandidatesSyncResponse>({
    queryKey: ["/api/sync/notion/candidates"],
  });

  const { data: stagesData } = useQuery<
    { id: string; name: string; order: number; type?: string }[]
  >({
    queryKey: ["/api/job-openings", selectedJobId, "stages-for-sync"],
    queryFn: async () => {
      if (!selectedJobId) return [];
      const res = await fetch(`/api/sync/notion/jobs/${selectedJobId}/candidates`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.stages || [];
    },
    enabled: !!selectedJobId,
  });

  const importMutation = useMutation({
    mutationFn: async ({
      notionId,
      jobOpeningId,
      stageId,
    }: {
      notionId: string;
      jobOpeningId: string;
      stageId: string;
    }) => {
      const res = await apiRequest("POST", `/api/sync/notion/candidates/${notionId}/import`, {
        jobOpeningId,
        stageId: stageId || null,
      });
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Candidato importado",
        description: `"${data.applicant.fullName}" importado com sucesso.`,
      });
      setImportDialog(null);
      setSelectedJobId("");
      setSelectedStageId("");
      queryClient.invalidateQueries({ queryKey: ["/api/sync/notion/candidates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sync/notion/status"] });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao importar", description: err.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }

  const candidates = data?.candidates || [];
  const localJobs = data?.localJobs || [];
  const newCandidates = candidates.filter((c) => !c.syncState && !c.existingApplicant);
  const importedCandidates = candidates.filter(
    (c) => c.syncState?.status === "imported" || c.existingApplicant,
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {candidates.length} candidatos no Notion ({newCandidates.length} novos,{" "}
          {importedCandidates.length} já importados)
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          data-testid="button-refresh-candidates"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {newCandidates.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
            Novos ({newCandidates.length})
          </h3>
          {newCandidates.map((cand) => (
            <CandidateCard
              key={cand.notionId}
              candidate={cand}
              onImport={() => {
                setImportDialog({ candidate: cand });
                if (cand.linkedJob) setSelectedJobId(cand.linkedJob.id);
              }}
            />
          ))}
        </div>
      )}

      {importedCandidates.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
            Já Importados ({importedCandidates.length})
          </h3>
          {importedCandidates.map((cand) => (
            <CandidateCard key={cand.notionId} candidate={cand} imported />
          ))}
        </div>
      )}

      {candidates.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            Nenhum candidato encontrado no Notion.
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!importDialog}
        onOpenChange={(open) => {
          if (!open) {
            setImportDialog(null);
            setSelectedJobId("");
            setSelectedStageId("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importar Candidato</DialogTitle>
            <DialogDescription>
              Importar "{importDialog?.candidate.importData.name}" para a plataforma.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {importDialog?.candidate.context.status && (
              <div className="text-sm">
                <span className="text-muted-foreground">Status no Notion:</span>{" "}
                <Badge variant="outline">{importDialog.candidate.context.status}</Badge>
              </div>
            )}
            {importDialog?.candidate.positionInfo && (
              <div className="text-sm">
                <span className="text-muted-foreground">Posição no Notion:</span>{" "}
                {importDialog.candidate.positionInfo.name}
              </div>
            )}
            <div>
              <span className="text-sm font-medium mb-2 block">Vaga de destino *</span>
              <SearchableSelect
                options={localJobs.map((j) => ({ value: j.id, label: j.title }))}
                value={selectedJobId}
                onValueChange={(v) => {
                  setSelectedJobId(v);
                  setSelectedStageId("");
                }}
                placeholder="Selecione uma vaga..."
                data-testid="select-import-job"
              />
            </div>
            {selectedJobId && (
              <div>
                <span className="text-sm font-medium mb-2 block">Etapa de entrada</span>
                <SearchableSelect
                  options={(stagesData || []).map((s) => ({ value: s.id, label: s.name }))}
                  value={selectedStageId}
                  preserveOrder
                  onValueChange={setSelectedStageId}
                  placeholder="Selecione uma etapa (opcional)..."
                  data-testid="select-import-stage"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setImportDialog(null);
                setSelectedJobId("");
                setSelectedStageId("");
              }}
              data-testid="button-cancel-import"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (importDialog && selectedJobId) {
                  importMutation.mutate({
                    notionId: importDialog.candidate.notionId,
                    jobOpeningId: selectedJobId,
                    stageId: selectedStageId,
                  });
                }
              }}
              disabled={!selectedJobId || importMutation.isPending}
              data-testid="button-confirm-import"
            >
              {importMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CandidateCard({
  candidate,
  onImport,
  imported,
}: {
  candidate: NotionCandidate;
  onImport?: () => void;
  imported?: boolean;
}) {
  return (
    <Card data-testid={`card-candidate-${candidate.notionId}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{candidate.importData.name}</span>
              {imported && (
                <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-[hsl(var(--forest-pulse))]">
                  <Check className="w-3 h-3" />
                  Importado
                </span>
              )}
            </div>
            <div className="text-sm text-muted-foreground flex gap-3 flex-wrap">
              {candidate.importData.email && <span>{candidate.importData.email}</span>}
              {candidate.context.status && (
                <Badge variant="outline" className="text-xs">
                  {candidate.context.status}
                </Badge>
              )}
              {candidate.positionInfo && <span>Vaga: {candidate.positionInfo.name}</span>}
              {candidate.context.source && <span>Fonte: {candidate.context.source}</span>}
            </div>
          </div>
          {!imported && onImport && (
            <Button
              size="sm"
              onClick={onImport}
              data-testid={`button-import-candidate-${candidate.notionId}`}
            >
              <Plus className="w-4 h-4 mr-1" />
              Importar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
